import {
  forwardRef,
  FunctionComponent,
  PropsWithoutRef,
  Ref,
  ReactNode,
  ChangeEventHandler,
  MouseEvent
} from 'react';
import styled, { css } from 'styled-components';

import { defaultThemeProp } from '../../theme';
import { Action, ForwardProps } from '../../types';
import FormField from '../FormField';
import { StyledFormControl, FormControlProps } from '../FormControl';
import { useDirection, useUID } from '../../hooks';

export interface SelectProps {
  /** Pass Options components as children to render the options of the Select list. */
  children: ReactNode;
  /** Called when an Option is chosen within the list. */
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  /**
   * Sets DOM id for the control and associates label element via 'for' attribute.
   * If an id is not pass, a random id will be generated for any render.
   */
  id?: FormControlProps['id'];
  /** Set visual state based on a validation state. */
  status?: FormControlProps['status'];
  /** Pass a string or a fragment with an Icon and string. */
  label?: FormControlProps['label'];
  /** Visually hides the label region. */
  labelHidden?: FormControlProps['labelHidden'];
  /** It is recommended to pass a simple string to offer guidance. Text will be styled based on status prop. */
  info?: FormControlProps['info'];
  /** Indicate if the field is required. The browser defaults to false. */
  required?: FormControlProps['required'];
  /**
   * Creates a controlled input and sets the value. Requires an onChange handler to update value.
   * value + onChange is the recommended method per React team.
   */
  value?: FormControlProps['value'];
  /** Disable the control. The browser defaults to false. */
  disabled?: FormControlProps['disabled'];
  /** Makes the input non editable and non clickable. The browser defaults to false. */
  readOnly?: FormControlProps['readOnly'];
  /** Sets html name attribute for the underlying control. Useful for mapping to a data field. */
  name?: FormControlProps['name'];
  /** Pass an array of Action objects to append button(s) inline with the Select. */
  actions?: Action[];
  /** A ref to the select element. */
  ref?: Ref<HTMLSelectElement>;
}

export const StyledSelect = styled.select<{ status: boolean }>(props => {
  const {
    base,
    components: {
      'form-control': { 'foreground-color': fg },
      select
    }
  } = props.theme;

  const foreground = encodeURIComponent(fg);
  const { rtl } = useDirection();

  return css`
    appearance: none;
    -webkit-appearance: none;
    overflow-x: hidden;
    text-overflow: ellipsis;
    width: 100%;
    height: ${select.height};
    min-height: ${base['hit-area']['mouse-min']};
    padding-inline-start: ${select.padding};
    padding-inline-end: calc(4 * ${base.spacing});

    /* cSpell:disable-next-line */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 25 25' fill='${foreground}'%3E%3Cpath d='M12.1476563,16.5726562 C12.1476563,16.5726562 3.2875,9.72421875 3.2875,9.72421875 C3.2875,9.72421875 3.14375,9.58046875 3.14375,9.58046875 C3.04765625,9.3890625 3,9.19765625 3,9.00546875 C3,9.00546875 3,9.00546875 3,9.00546875 C3,8.33515625 3.33515625,8 3.9578125,8 C3.9578125,8 3.9578125,8 3.9578125,8 C4.1015625,8 4.29296875,8.09609375 4.628125,8.2390625 C4.628125,8.2390625 4.628125,8.2390625 4.628125,8.2390625 C4.628125,8.2390625 12.8179688,14.2257813 12.8179688,14.2257813 C12.8179688,14.2257813 21.103125,8.19140625 21.103125,8.19140625 C21.3421875,8.04765625 21.534375,8 21.678125,8 C21.678125,8 21.678125,8 21.678125,8 C22.3007813,8 22.6359375,8.33515625 22.6359375,9.00546875 C22.6359375,9.00546875 22.6359375,9.00546875 22.6359375,9.00546875 C22.6359375,9.196875 22.5882813,9.38828125 22.4921875,9.58046875 C22.4921875,9.58046875 22.4921875,9.58046875 22.4921875,9.58046875 C22.4921875,9.58046875 22.3484375,9.72421875 22.3484375,9.72421875 C22.3484375,9.72421875 13.4882812,16.525 13.4882812,16.525 C13.296875,16.7164063 13.0570312,16.8125 12.8179688,16.8125 C12.8179688,16.8125 12.8179688,16.8125 12.8179688,16.8125 C12.5789062,16.8125 12.3867188,16.7164062 12.1476563,16.5734375 C12.1476563,16.5734375 12.1476563,16.5734375 12.1476563,16.5734375 L12.1476563,16.5726562 Z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-size: calc(2 * ${base.spacing}) auto;
    background-position: ${rtl ? base.spacing : `calc(100% - ${base.spacing}) `} 60%;
    border-width: ${select['border-width']};
    border-radius: calc(${base['border-radius']} * ${select['border-radius']});

    ${!props.status &&
    css`
      border-color: ${select['border-color']};
    `}

    &::-ms-expand {
      display: none;
    }

    @media (pointer: coarse) {
      min-height: ${base['hit-area']['finger-min']};
    }
  `;
});

StyledSelect.defaultProps = defaultThemeProp;

const Select: FunctionComponent<SelectProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<SelectProps>, ref: SelectProps['ref']) => {
    const uid = useUID();
    const {
      children,
      id = uid,
      label,
      labelHidden,
      info,
      status,
      required = false,
      disabled = false,
      readOnly = false,
      actions,
      ...restProps
    } = props;

    const Comp = (
      <StyledFormControl
        {...restProps}
        as={StyledSelect}
        id={id}
        status={status}
        required={required}
        disabled={disabled}
        readOnly={readOnly}
        aria-readonly={readOnly ? 'true' : undefined}
        ref={ref}
        onMouseDown={
          readOnly
            ? (e: MouseEvent<HTMLSelectElement>) => {
                e.preventDefault();
              }
            : undefined
        }
        onKeyDown={
          readOnly
            ? (e: KeyboardEvent) => {
                // TODO: FOr future consider key modifiers at play e.g. "ctrl p"
                if (!['Escape', 'Tab'].includes(e.key)) e.preventDefault();
              }
            : undefined
        }
      >
        {children}
      </StyledFormControl>
    );

    return label ? (
      <FormField
        {...{
          label,
          labelHidden,
          id,
          info,
          status,
          required,
          disabled,
          readOnly,
          actions
        }}
      >
        {Comp}
      </FormField>
    ) : (
      Comp
    );
  }
);

export default Select;
